import type { IUtility } from '@/domain/entities/utility';
import { normalizeDeep } from '@/lib/dataSanity';
import { getApiEndpoints } from '@/services/api/endpoints';
import type { CreateItemResponse, ListUtilitiesResponse } from '@/services/api/types';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';

// -------------------------
// Fetch list by property
// -------------------------
export const fetchUtilitiesByPropertyId = createAppAsyncThunk<IUtility[], { propertyID: string }>(
  'propertyUtilities/fetchByPropertyId',
  async ({ propertyID }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint =
        getApiEndpoints().core.propertyUtilities.getUtilities + `?propertyID=${encodeURIComponent(propertyID)}`;
      const res = await apiService.get<ListUtilitiesResponse>(endpoint);
      return Array.isArray(res.items) ? res.items : [];
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

// -------------------------
// Fetch one
// -------------------------
export const fetchUtilityById = createAppAsyncThunk<IUtility, { id: string }>(
  'propertyUtilities/fetchById',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.propertyUtilities.getUtilities + `?id=${encodeURIComponent(id)}`;
      return await apiService.get<IUtility>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

// -------------------------
// Create
// -------------------------
export const createUtility = createAppAsyncThunk<CreateItemResponse<IUtility>, { utility: IUtility; tempId?: string }>(
  'propertyUtilities/create',
  async ({ utility }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.propertyUtilities.createUtility;
      return await apiService.post<CreateItemResponse<IUtility>>(endpoint, normalizeDeep(utility));
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

// -------------------------
// Update
// -------------------------
export const updateUtility = createAppAsyncThunk<
  IUtility,
  { id: string; propertyID: string; patch: Partial<IUtility> }
>('propertyUtilities/update', async ({ id, propertyID, patch }, { extra: { config }, rejectWithValue }) => {
  const { apiService } = config;

  try {
    const endpoint =
      getApiEndpoints().core.propertyUtilities.updateUtility +
      `?id=${encodeURIComponent(id)}&propertyID=${encodeURIComponent(propertyID)}`;
    return await apiService.put<IUtility>(endpoint, normalizeDeep(patch));
  } catch (err) {
    const e = err as Error & { status?: number; body?: string };
    return rejectWithValue({ message: e.message });
  }
});

// -------------------------
// Delete
// -------------------------
export const deleteUtility = createAppAsyncThunk<{ id: string }, { id: string; propertyID: string }>(
  'propertyUtilities/delete',
  async ({ id, propertyID }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint =
        getApiEndpoints().core.propertyUtilities.deleteUtility +
        `?id=${encodeURIComponent(id)}&propertyID=${encodeURIComponent(propertyID)}`;
      return await apiService.delete<{ id: string }>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);
